import { useState, useEffect } from "react";
import axios from "../../utils/axiosInstance";
import MonthlyReportTable from "./MonthlyReportTable";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MonthlyPerGroup = ({ group }) => {
  const [devices, setDevices] = useState([]); // full devices with {id, name}
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // SAME MONTH LOGIC
  const getLastSixMonths = () => {
    const allMonths = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date();
    const currentMonth = now.getMonth();
    const result = [];
    for (let i = 1; i <= 6; i++) {
      const d = new Date(now.getFullYear(), currentMonth - i, 1);
      result.push({
        label: `${allMonths[d.getMonth()]} ${d.getFullYear()}`,
        value: d.getMonth() + 1,
        year: d.getFullYear(),
      });
    }
    return result.reverse();
  };

  const lastSixMonths = getLastSixMonths();
  const [selectedMonth, setSelectedMonth] = useState(lastSixMonths[5].value);
  const [selectedYear, setSelectedYear] = useState(lastSixMonths[5].year);

  // Fetch devices for the group
  useEffect(() => {
    if (!group?.id) return;
    const fetchDevices = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${group.id}`,
        );
        setDevices(response.data || []);
      } catch (err) {
        console.error("Error fetching devices:", err);
      }
    };
    fetchDevices();
  }, [group?.id]);

  // Fetch report for group devices whenever devices, selectedMonth, or selectedYear change
  useEffect(() => {
    if (devices.length > 0) {
      fetchMonthlyReport(selectedMonth, selectedYear);
    }
  }, [devices, selectedMonth, selectedYear]);

  const fetchMonthlyReport = async (month, year) => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        year,
        month,
        units: devices.map((d) => d.id),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-month`,
        payload,
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );

      setReportData(response.data.data.mydict);
    } catch (err) {
      console.error(err);
      setError("Failed to load monthly report");
    } finally {
      setLoading(false);
    }
  };

  const unitMap = devices.map((u) => ({ id: u.id, name: u.name }));
  const getUnitName = (unitId) => {
    const unit = unitMap.find((u) => u.id === unitId);
    return unit ? unit.name : unitId;
  };

  const filteredReportData = reportData
    ? Object.entries(reportData).map(([unitId, report]) => ({
        ...report,
        unitId,
        unitName: getUnitName(unitId),
        monthName: new Date(report.year, report.month - 1).toLocaleString(
          "default",
          { month: "long" },
        ),
      }))
    : [];

  const filteredReportDataWithSearch = filteredReportData.filter((report) => {
    const query = searchQuery.toLowerCase();
    return (
      report.unitName.toLowerCase().includes(query) ||
      report.year.toString().includes(query) ||
      report.monthName.toLowerCase().includes(query) ||
      report.down.toString().includes(query) ||
      report.up.toString().includes(query) ||
      report.customer.toString().includes(query) ||
      report.precision.toString().includes(query) ||
      report.shutDowns.toString().includes(query)
    );
  });

  // Export functions
  const exportToCSV = () => {
    if (!filteredReportData || filteredReportData.length === 0) return;

    const csvContent = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop(Hrs)",
        "Run(Hrs)",
        "Customer(Hrs)",
        "PRC(Hrs)",
        "Shutdowns",
      ],
      ...filteredReportData.map((report) => [
        report.unitName, // use unitName from object
        report.year,
        report.month,
        report.down,
        report.up,
        report.customer,
        report.precision,
        report.shutDowns,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((f) => `"${f}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Monthly_Report.csv`;
    link.click();
  };

  const exportToExcel = () => {
    if (!filteredReportData || filteredReportData.length === 0) return;

    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Stop(Hrs)",
      "Run(Hrs)",
      "Customer(Hrs)",
      "PRC(Hrs)",
      "Shutdowns",
    ];

    const rows = filteredReportData.map((report) => [
      report.unitName, // use unitName from object
      report.year,
      report.month,
      report.down,
      report.up,
      report.customer,
      report.precision,
      report.shutDowns,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Report");
    XLSX.writeFile(workbook, "Monthly_Report.xlsx");
  };

  const exportToPDF = () => {
    if (!filteredReportData || filteredReportData.length === 0) return;

    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text("Monthly Units Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Stop(Hrs)",
        "Run(Hrs)",
        "Customer(Hrs)",
        "PRC(Hrs)",
        "Shutdowns",
      ],
    ];

    const rows = filteredReportData.map((report) => [
      report.unitName, // use unitName from object
      report.year,
      new Date(report.year, report.month - 1).toLocaleString("default", {
        month: "long",
      }),
      report.down,
      report.up,
      report.customer,
      report.precision,
      report.shutDowns,
    ]);

    autoTable(doc, { head: headers, body: rows, startY: 50, theme: "grid" });
    doc.save("Monthly_Report.pdf");
  };
  return (
    <div>
      {/* Search + Month Selector + Export */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        {/* Search Input */}
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-3 py-2 w-full sm:w-64 rounded-md border text-white border-gray-300 bg-transparent placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Month Selector */}

        {/* Export Buttons */}
        <div className="flex flex-wrap gap-2">
          <select
            value={`${selectedMonth}-${selectedYear}`}
            onChange={(e) => {
              const [month, year] = e.target.value.split("-").map(Number);
              setSelectedMonth(month);
              setSelectedYear(year);
            }}
            className="px-3 py-2 rounded-md border text-white border-gray-300 bg-slate-700 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {lastSixMonths.map((m) => (
              <option
                key={`${m.value}-${m.year}`}
                value={`${m.value}-${m.year}`}
              >
                {m.label}
              </option>
            ))}
          </select>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[65px] sm:w-auto"
            onClick={exportToCSV}
          >
            CSV
          </button>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[65px] sm:w-auto"
            onClick={exportToExcel}
          >
            Excel
          </button>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-[60px] sm:w-auto"
            onClick={exportToPDF}
          >
            PDF
          </button>
        </div>
      </div>

      <MonthlyReportTable
        reportData={filteredReportDataWithSearch}
        units={devices}
        loading={loading}
        error={error}
      />
    </div>
  );
};

export default MonthlyPerGroup;
